import React from "react";
import PropTypes from "prop-types";
import "./RenderUtils.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from 'recharts';


export const renderProgressBar = (type, value) => {
  // clamp and sanitize value
  let numValue = Number(parseFloat(value));
  if (Number.isNaN(numValue)) numValue = 0;
  numValue = Math.max(0, Math.min(100, Math.round(numValue)));

  // choose a subtle gradient per type
  const gradients = {
    traditional: "linear-gradient(90deg, #f6a98c 0%, #f09376 60%)",
    biobased: "linear-gradient(90deg, #9ad6c4 0%, #0f6c65 60%)",
    default: "linear-gradient(90deg, #d6e0db 0%, #bfcfc7 60%)",
  };
  const bg = gradients[type] || gradients.default;

  // simple, non-animated progress bar with external percentage label
  return (
    <div
      className={`${type}-progress progress-wrapper`}
      role="group"
      aria-label={`${type} percentage ${numValue}%`}
    >
      <div className="progress-track" aria-hidden="true">
        <div
          className="progress-fill"
          style={{
            width: `${numValue}%`,
            backgroundImage: bg,
          }}
        />
      </div>
      <div className="progress-value" aria-hidden="false">
        {`${numValue}%`}
      </div>
    </div>
  );
};



export const renderSquares = (type, value) => {
  const numValue = parseFloat(value);
  if (isNaN(numValue)) {
    return <div className="squares-container-undefined">-</div>;
  }

  const squares = [];
  for (let i = 0; i < 5; i++) {
    squares.push(
      <div
        key={i}
        className={`${type}-square ${i < numValue ? "filled" : "empty"}`}
      />
    );
  }

  return <div className="squares-container">{squares}</div>;
};


export const formatData = (type, data) => {
  if (type !== "Herkomst" && type !== "Circulariteit") {
    console.log("Invalid type");
    return null;
  }
   else if (type === "Herkomst") {
      return [
        {
          nieuw: parseFloat(data.qualities_herkomst_nieuw) || 0,
          biobased: parseFloat(data.qualities_herkomst_biobased) || 0,
          gerecycled: parseFloat(data.qualities_herkomst_gerecycled) || 0,
          hergebruikt: parseFloat(data.qualities_herkomst_hergebruikt) || 0,
          name: ""
        }
      ];
   }
    else if (type === "Circulariteit") {
      return [
        {
          stort: parseFloat(data.qualities_circulariteit_stort) || 0,
          verbranden: parseFloat(data.qualities_circulariteit_verbranden) || 0,
          recyclen: parseFloat(data.qualities_circulariteit_recyclen) || 0,
          hergebruiken: parseFloat(data.qualities_circulariteit_hergebruiken) || 0,
          name: ""
        }
      ];
    }      
};


export const StackedBarChartHorizontal = ({ data, type }) => {
  // defensive fallback
  const chartData = Array.isArray(data) && data.length ? data : [{ name: "" }];

  const COLORS = {
    nieuw: "#f09376",
    biobased: "#ebdcc2",
    gerecycled: "#d6e0db",
    hergebruikt: "#0f6c65",
    stort: "#f09376",
    verbranden: "#ebdcc2",
    recyclen: "#d6e0db",
    hergebruiken: "#0f6c65",
  };

  // show labels only for segments >= 8% to avoid overlap on narrow bars
  const centerLabel = (val) => {
    const n = Number(val) || 0;
    return n >= 8 ? `${Math.round(n)}%` : "";
  };

  const bars = type === "Herkomst"
    ? [
        <Bar key="nieuw" dataKey="nieuw" stackId="a" fill={COLORS.nieuw} radius={[0,0,0,0]} barSize={28} isAnimationActive animationDuration={500}><LabelList dataKey="nieuw" position="center" formatter={centerLabel} /></Bar>,
        <Bar key="biobased" dataKey="biobased" stackId="a" fill={COLORS.biobased} radius={[0,0,0,0]} barSize={28} isAnimationActive animationDuration={500}><LabelList dataKey="biobased" position="center" formatter={centerLabel} /></Bar>,
        <Bar key="gerecycled" dataKey="gerecycled" stackId="a" fill={COLORS.gerecycled} radius={[0,0,0,0]} barSize={28} isAnimationActive animationDuration={500}><LabelList dataKey="gerecycled" position="center" formatter={centerLabel} /></Bar>,
        <Bar key="hergebruikt" dataKey="hergebruikt" stackId="a" fill={COLORS.hergebruikt} radius={[0,0,0,0]} barSize={28} isAnimationActive animationDuration={500}><LabelList dataKey="hergebruikt" position="center" formatter={centerLabel} /></Bar>,
      ]
    : [
        <Bar key="stort" dataKey="stort" stackId="a" fill={COLORS.stort} radius={[0,0,0,0]} barSize={28} isAnimationActive animationDuration={500}><LabelList dataKey="stort" position="center" formatter={centerLabel} /></Bar>,
        <Bar key="verbranden" dataKey="verbranden" stackId="a" fill={COLORS.verbranden} radius={[0,0,0,0]} barSize={28} isAnimationActive animationDuration={500}><LabelList dataKey="verbranden" position="center" formatter={centerLabel} /></Bar>,
        <Bar key="recyclen" dataKey="recyclen" stackId="a" fill={COLORS.recyclen} radius={[0,0,0,0]} barSize={28} isAnimationActive animationDuration={500}><LabelList dataKey="recyclen" position="center" formatter={centerLabel} /></Bar>,
        <Bar key="hergebruiken" dataKey="hergebruiken" stackId="a" fill={COLORS.hergebruiken} radius={[0,0,0,0]} barSize={28} isAnimationActive animationDuration={500}><LabelList dataKey="hergebruiken" position="center" formatter={centerLabel} /></Bar>,
      ];

  return (
    <div className="bar-chart" style={{ width: "100%", height: 160 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} layout="vertical" margin={{ top: 10, right: 12, left: 12, bottom: 36 }}>
          <CartesianGrid stroke="#f3f3f3" vertical={false} />
          <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" hide />
          <Tooltip formatter={(v) => `${v}%`} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
          <Legend verticalAlign="bottom" layout="horizontal" wrapperStyle={{ width: "100%", marginTop: 6, textAlign: "center" }} iconType="square" />
          {bars}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

StackedBarChartHorizontal.propTypes = {
  data: PropTypes.array,
  type: PropTypes.string.isRequired,
};
